import { Helmet } from "react-helmet-async";

import { BRAND, SITE_DESCRIPTION, SITE_URL } from "@/lib/site";

interface SEOProps {
  title: string;
  description?: string;
  path: string;
  image?: string;
  noindex?: boolean;
  /**
   * JSON-LD object(s) to inject for structured data. Each will be rendered in
   * its own <script type="application/ld+json"> tag — keep them as plain objects
   * and we'll stringify.
   */
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
}

/**
 * Centralized head-tag helper.
 *
 * SEO decisions baked in:
 *   - Title pattern: "<Page> — <Brand>" (only homepage uses just the brand).
 *   - Description falls back to SITE_DESCRIPTION.
 *   - Open Graph + Twitter card are emitted as a pair.
 *   - Canonical is derived from SITE_URL + path (no trailing slash except root).
 *   - Any JSON-LD passed is injected as <script> blocks for rich results.
 *
 * Accessibility note: titles appear in screen-reader page announcements via
 * native browser behavior; keep them descriptive but concise.
 */
export function SEO({ title, description, path, image, noindex, jsonLd }: SEOProps) {
  const fullTitle = path === "/" ? `${title} — ${BRAND}` : `${title} — ${BRAND}`;
  const desc = description ?? SITE_DESCRIPTION;
  const canonical = `${SITE_URL}${path === "/" ? "" : path}`;
  const ogImage = image ?? `${SITE_URL}/og-default.png`;

  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLdArray.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
